const plans = ['Starter', 'Growth', 'Enterprise'];
const statuses = ['active', 'trial', 'suspended', 'at_risk'];
const industries = ['Technology', 'Finance', 'Healthcare', 'Retail', 'Manufacturing', 'Telecom', 'Government'];
const regions = ['MENA', 'EU', 'NA', 'APAC'];
const timezones = ['Asia/Muscat', 'Asia/Dubai', 'Europe/London', 'America/New_York'];

const pick = (arr, i) => arr[i % arr.length];

export const tenantsList = Array.from({ length: 48 }, (_, i) => {
  const plan = pick(plans, i);
  const status = pick(statuses, i + 1);
  return {
    id: `tnt-${1000 + i}`,
    tenantCode: `MH-${String(i + 1).padStart(4, '0')}`,
    companyName: `Acme Corp ${i + 1} LLC`,
    domain: `tenant${i + 1}.meetinghub.ai`,
    plan,
    status,
    onboardingStep: Math.min(6, (i % 6) + 1),
    totalUsers: 12 + (i % 20) * 3,
    lastActivity: new Date(Date.now() - i * 3600000 * 2).toISOString(),
    mrr: plan === 'Enterprise' ? 2499 + i * 20 : plan === 'Growth' ? 899 + i * 10 : 199 + i * 3,
    billingStatus: i % 9 === 0 ? 'past_due' : 'current',
    microsoftConnected: i % 3 !== 0,
    googleConnected: i % 4 !== 0,
    whatsappConnected: i % 2 === 0,
    createdAt: new Date(Date.now() - (i + 30) * 86400000).toISOString(),
    ownerName: `Owner ${i + 1} User`,
    ownerEmail: `owner${i + 1}@tenant${i + 1}.com`,
    industry: pick(industries, i),
    timezone: pick(timezones, i),
    region: pick(regions, i),
  };
});

export const tenantDetail = {
  ...tenantsList[0],
  seatsPurchased: 120,
  seatsUsed: 87,
  apiRateLimit: 1200,
  dataResidency: 'eu-west-1',
  ssoEnabled: true,
  notes: 'Strategic customer — quarterly business review scheduled.',
};

export const onboardingChecklist = [
  { id: 'chk-1', label: 'Verify company domain', done: true, completedAt: '2025-02-01T10:00:00Z' },
  { id: 'chk-2', label: 'Connect Microsoft 365', done: true, completedAt: '2025-02-02T14:30:00Z' },
  { id: 'chk-3', label: 'Connect Google Workspace', done: false },
  { id: 'chk-4', label: 'Configure WhatsApp Business', done: false },
  { id: 'chk-5', label: 'Import users via SCIM', done: true, completedAt: '2025-02-05T09:15:00Z' },
  { id: 'chk-6', label: 'Enable AI transcription', done: false },
];

/** 5-item checklist for tenant detail tab (spec copy) */
export const tenantOnboardingItems = [
  { id: 'o1', key: 'microsoft', label: 'Connect Microsoft', status: 'done' },
  { id: 'o2', key: 'google', label: 'Connect Google', status: 'pending' },
  { id: 'o3', key: 'whatsapp', label: 'Connect WhatsApp', status: 'warning' },
  { id: 'o4', key: 'teams_app', label: 'Upload Teams App', status: 'pending' },
  { id: 'o5', key: 'booking', label: 'Test Booking', status: 'pending' },
];

export function buildTenantSubscription(t) {
  return {
    planName: t.plan,
    contractTerm: '12 months',
    billingCycle: 'monthly',
    amountUsd: t.mrr,
    renewalDate: new Date(Date.now() + 120 * 86400000).toISOString(),
    discountPct: t.plan === 'Enterprise' ? 10 : 0,
  };
}

export function buildTenantIntegrations(t) {
  return {
    microsoft: {
      tokenStatus: t.microsoftConnected ? 'valid' : 'missing',
      lastSync: t.microsoftConnected ? new Date(Date.now() - 3600000).toISOString() : null,
      webhookStatus: t.microsoftConnected ? 'subscribed' : 'inactive',
    },
    google: {
      tokenStatus: t.googleConnected ? 'valid' : 'missing',
      lastSync: t.googleConnected ? new Date(Date.now() - 7200000).toISOString() : null,
      webhookStatus: t.googleConnected ? 'subscribed' : 'inactive',
    },
    whatsapp: {
      tokenStatus: t.whatsappConnected ? 'valid' : 'missing',
      lastSync: t.whatsappConnected ? new Date(Date.now() - 1800000).toISOString() : null,
      webhookStatus: t.whatsappConnected ? 'verified' : 'inactive',
    },
  };
}

export function buildTenantUsage(t) {
  const limits = {
    Starter: { u: 50, m: 500, tok: 1, st: 25 },
    Growth: { u: 250, m: 3000, tok: 3, st: 200 },
    Enterprise: { u: 2000, m: 20000, tok: 20, st: 2000 },
  };
  const L = limits[t.plan] || limits.Starter;
  const idNum = parseInt(String(t.id).replace(/\D/g, ''), 10) || 0;
  return {
    users: { used: t.totalUsers, limit: L.u },
    meetings: { used: 120 + (idNum % 800), limit: L.m },
    aiTokensM: { used: Math.round((1.2 + (idNum % 10) * 0.1) * 10) / 10, limit: L.tok },
    storageGb: { used: 18 + (idNum % 40), limit: L.st },
  };
}

export function buildSupportHistory(tenantId) {
  const idx = parseInt(String(tenantId).replace(/\D/g, ''), 10) || 0;
  return [
    {
      id: `sh-${idx}-1`,
      date: new Date(Date.now() - 86400000 * 14).toISOString(),
      admin: 'support1@mh.ai',
      reason: 'Onboarding assist',
      durationMin: 45,
      outcome: 'resolved',
      piiMasked: true,
    },
    {
      id: `sh-${idx}-2`,
      date: new Date(Date.now() - 86400000 * 40).toISOString(),
      admin: 'support3@mh.ai',
      reason: 'Integration health check',
      durationMin: 30,
      outcome: 'resolved',
      piiMasked: true,
    },
  ];
}

export function buildTenantAuditMini(tenantId) {
  return Array.from({ length: 20 }, (_, i) => ({
    id: `taud-${tenantId}-${i}`,
    ts: new Date(Date.now() - i * 7200000).toISOString(),
    actor: `admin${i % 4}@mh.ai`,
    action: ['CONFIG_CHANGE', 'LOGIN', 'EXPORT', 'ROLE_ASSIGN'][i % 4],
    result: i % 11 === 0 ? 'denied' : 'allowed',
  }));
}

export const lastSupportActionTemplate = {
  date: new Date(Date.now() - 86400000 * 3).toISOString(),
  admin: 'support2@mh.ai',
  summary: 'Verified WhatsApp webhook delivery and template namespace.',
};
